import React, { useEffect, useRef, useState, ElementType } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Slide,
  Stack,
  AppBarProps,
  ButtonProps,
  IconButtonProps,
  MenuProps,
  MenuItemProps
} from "@mui/material";
import muiStyled from "@mui/system/styled";
import NotificationComponent from "./atomic/NotificationComponent";
import { getBaseUrl } from "./LandingOne";

const NavBar = ({
  isMain = true,
  isSticky = false,
  bgColor = "linear-gradient(89deg, #002671 15.54%, #002366 41.96%, #001e51 76.45%)",
  isNotification = true,
  showLogin = false
}) => {
  const signInUrl = getBaseUrl();
  const [mobileMenuAnchorEl, setMobileMenuAnchorEl] =
    useState<null | HTMLElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const navbarItem = [
    { title: "Home", link: "/" },
    { title: "About Us", link: "/about-us" },
    { title: "Careers", link: "/careers" }
  ];

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMenuAnchorEl(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchorEl(null);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
      setMobileMenuAnchorEl(null);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <NavBarContainer direction={"column"} spacing={"32px"}>
      {isNotification && <NotificationComponent />}

      <StyledAppBar isSticky={isSticky} bgColor={bgColor}>
        <StyledToolBar>
          <NavBarImage
            src={isMain ? "assets/img/logo.png" : "assets/img/logo_dark.png"}
            alt="majura-logo"
          />

          <NavBarButtonContainer>
            {navbarItem.map((data) => (
              <NavBarButton
                key={data.title}
                component={Link}
                to={data.link}
                isMain={isMain}
              >
                {data.title}
              </NavBarButton>
            ))}

            {showLogin && (
              <LoginButton href={signInUrl} isMain={isMain}>
                Log in
              </LoginButton>
            )}
          </NavBarButtonContainer>

          <HamburgerContainer>
            <HamburgerIconButton onClick={handleMobileMenuOpen} isMain={isMain}>
              <img
                src="/assets/icon/icons/hamburger.svg"
                alt="Menu Icon"
                width="24"
                height="24"
              />
            </HamburgerIconButton>

            <StyledMenu
              id="mobile-menu"
              anchorEl={mobileMenuAnchorEl}
              anchorOrigin={{
                vertical: "top",
                horizontal: "right"
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right"
              }}
              slots={{
                transition: Slide
              }}
              open={Boolean(mobileMenuAnchorEl)}
              onClose={handleMobileMenuClose}
            >
              <CloseIconContainer ref={menuRef}>
                <ClosedIconButton onClick={handleMobileMenuClose}>
                  <img
                    src="/assets/icon/icons/close.svg"
                    alt="Close Icon"
                    width="24"
                    height="24"
                  />
                </ClosedIconButton>
              </CloseIconContainer>

              {navbarItem.map((data) => (
                <MobileMenuItem
                  key={data.title}
                  onClick={handleMobileMenuClose}
                  component={Link}
                  to={data.link}
                >
                  {data.title}
                </MobileMenuItem>
              ))}
            </StyledMenu>
          </HamburgerContainer>
        </StyledToolBar>
      </StyledAppBar>
    </NavBarContainer>
  );
};

const NavBarContainer = muiStyled(Stack)(({ theme }) => ({
  position: "relative",
  width: "100%"
}));

interface StyledAppBarProps extends AppBarProps {
  isSticky?: boolean;
  bgColor?: string;
}

const StyledAppBar = muiStyled(AppBar, {
  shouldForwardProp: (prop) => prop !== "isSticky" && prop !== "bgColor"
})<StyledAppBarProps>(({ theme, isSticky, bgColor }) => ({
  top: 0,
  height: "125px",
  maxHeight: "125px !important",
  left: 0,
  zIndex: 99,
  background: bgColor,
  boxShadow: "none",
  justifyContent: "center",
  position: "absolute",
  [theme.breakpoints.up("md")]: {
    position: !isSticky ? "absolute" : "fixed"
  }
}));

const StyledToolBar = muiStyled(Toolbar)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "0px 24px !important",
  [theme.breakpoints.up("md")]: {
    padding: "0px 48px !important"
  }
}));

const NavBarImage = muiStyled("img")(({ theme }) => ({
  height: "auto",
  maxWidth: "125px",
  [theme.breakpoints.up("sm")]: {
    maxWidth: "150px"
  },
  [theme.breakpoints.up("md")]: {
    maxWidth: "175px"
  },
  [theme.breakpoints.up("lg")]: {
    maxWidth: "200px"
  },
  [theme.breakpoints.up("xl")]: {
    maxWidth: "200px"
  }
}));

const NavBarButtonContainer = muiStyled(Box)(({ theme }) => ({
  gap: "40px",
  display: "none",
  [theme.breakpoints.up("md")]: {
    display: "flex"
  }
}));

interface NavBarButtonProps extends ButtonProps {
  isMain?: boolean;
  to?: string;
}

const NavBarButton = muiStyled(Button, {
  shouldForwardProp: (prop) => prop !== "isMain"
})<NavBarButtonProps>(({ isMain }) => ({
  color: isMain ? "white" : "#002671",
  fontSize: "1.2em",
  borderRadius: "12px",
  textTransform: "none",
  padding: "8px 4px",
  "&:hover": {
    backgroundColor: "transparent",
    color: isMain ? "#40EEFF" : "#002671"
  },
  "&:focus": {
    color: isMain ? "#40EEFF" : "#002671"
  }
}));

const LoginButton = muiStyled("a", {
  shouldForwardProp: (prop) => prop !== "isMain"
})<{ isMain?: boolean }>(({ isMain }) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "8px 22px",
  borderRadius: "999px",
  fontSize: "0.95em",
  fontWeight: 600,
  textDecoration: "none",
  border: `1px solid ${isMain ? "rgba(255, 255, 255, 0.5)" : "#c7d0e6"}`,
  color: isMain ? "#ffffff" : "#002671",
  "&:hover": {
    borderColor: isMain ? "#ffffff" : "#002671"
  }
}));

const HamburgerContainer = muiStyled(Box)(({ theme }) => ({
  display: "flex",
  [theme.breakpoints.up("md")]: {
    display: "none"
  }
}));

interface IconButtonComps extends IconButtonProps {
  isMain?: boolean;
}

const HamburgerIconButton = muiStyled(IconButton, {
  shouldForwardProp: (prop) => prop !== "isMain"
})<IconButtonComps>(({ theme, isMain }) => ({
  color: isMain ? "#77EBFF" : "#002671",
  margin: 0
}));

const StyledMenu = muiStyled(Menu)<MenuProps>(({ theme }) => ({
  "& .MuiPaper-root": {
    maxWidth: "100%",
    backgroundColor: "#000",
    padding: "24px",
    top: "0px !important",
    left: "0px !important",
    right: "0px !important",
    borderRadius: 0
  }
}));

const CloseIconContainer = styled.div`
  display: flex;
  justify-content: end;
  align-items: right;
`;

const ClosedIconButton = muiStyled(IconButton)(({ theme }) => ({
  color: "red",
  margin: 0,
  alignItems: "right"
}));

interface MobileMenuItemProps extends MenuItemProps {
  component?: ElementType;
  to?: string;
}

const MobileMenuItem = muiStyled(MenuItem, {
  shouldForwardProp: (prop) => prop !== "isMain"
})<MobileMenuItemProps>(({ theme }) => ({
  color: "white",
  fontSize: "1.2em",
  textTransform: "none",
  padding: "16px 12px",
  justifyContent: "left"
}));

export default NavBar;
